// chatbot-admin/ui/client/src/components/Header.js
import React, { useContext, useState, useEffect, useCallback } from "react";
import { withRouter } from "react-router";
import { useMyContext } from "../MyContext";
import { Navbar, Nav, NavDropdown } from "react-bootstrap";
import { IndexLinkContainer } from "react-router-bootstrap";
import { Link, useLocation, useHistory } from "react-router-dom";
import { auth } from "../firebase/config";
import { useAuthState } from "react-firebase-hooks/auth";
import GlobalContext from "../GlobalContext";
import { fetchSecure, logUserAction, sendChatMessage } from "../utils/utils";
import { FaCircleUser, FaAngleDown } from "react-icons/fa6";
import { AiOutlineClose, AiOutlineLogout } from "react-icons/ai";
import { IoMdClose } from "react-icons/io";
import SuperAppButton from "./SuperAppButton";
import MenuTreeEditor from "./MenuTreeEditor";
import MenuBuilderModal from "./MenuBuilderModal";
import ThemePickerModal from "./ThemePickerModal";
import { FaEdit, FaEye, FaPalette } from "react-icons/fa";

// ✅ SuperApp config helpers
import {
  useSuperAppConfig,
  getSuperAppConfigSync,
  setSuperAppConfig,
  loadSuperAppConfig,
  createSuperAppConfig,
} from "../lib/superappConfig";

import { THEME_PRESETS } from "../ThemeContext";
import { Modal, Button, Form } from "react-bootstrap";
import { FaPlus } from "react-icons/fa";

function Header(props) {
  const [user, loading] = useAuthState(auth);
  const { data, authConfig } = useMyContext();
  const [activeMenu, setActiveMenu] = useState("/home");
  const [member, setMember] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [newPath, setNewPath] = useState("");
  const [newParent, setNewParent] = useState("");
  const [newOrder, setNewOrder] = useState(0);
  const [newVisible, setNewVisible] = useState(true);
  const [newOpenInNewTab, setNewOpenInNewTab] = useState(false);
  const [newBadge, setNewBadge] = useState("");
  const [newDisabled, setNewDisabled] = useState(false);
  const [newGroup, setNewGroup] = useState("");
  const [marketingMessages, setMarketingMessages] = useState([]);
  const [clickedMessages, setClickedMessages] = useState([]);
  const [afterLoginUserInfo, setAfterLoginUserInfo] = useState(false);

  // Edit Mode State
  const [editMode, setEditMode] = useState(false);
  const [showMenuBuilder, setShowMenuBuilder] = useState(false);
  const [showThemePicker, setShowThemePicker] = useState(false);

  // Create App Modal State
  const [showCreateAppModal, setShowCreateAppModal] = useState(false);
  const [createAppName, setCreateAppName] = useState("");
  const [createAppId, setCreateAppId] = useState("");
  const [createAppTheme, setCreateAppTheme] = useState("purple");
  const [creatingApp, setCreatingApp] = useState(false);

  const location = useLocation();
  const history = useHistory();
  const globalData = useContext(GlobalContext);
  const superAppStyle = globalData["superAppStyle"] || {
    menuActive: {},
    menuInactive: {},
    headerText: {},
  };

  const rawAppId = new URLSearchParams(window.location.search).get("appId");
  const APP_ID = rawAppId ? rawAppId.split("?")[0] : null;

  // ---- Live config + “version bump” so we update immediately after edits ----
  const hookCfg = useSuperAppConfig(APP_ID);
  const [cfgVersion, setCfgVersion] = useState(0);
  const [localConfigOverride, setLocalConfigOverride] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");

  useEffect(() => {
    const onConfigUpdated = async (e) => {
      if (!e?.detail?.appId || e.detail.appId === APP_ID) {
        // Force re-load from cache to get the latest config
        try {
          await loadSuperAppConfig(APP_ID);
        } catch (err) {
          console.warn("Failed to reload config after update:", err);
        }
        setCfgVersion((v) => v + 1);
      }
    };
    window.addEventListener("superapp-config-updated", onConfigUpdated);
    return () =>
      window.removeEventListener("superapp-config-updated", onConfigUpdated);
  }, [APP_ID]);

  // Most up-to-date snapshot from hook or cache
  // IMPORTANT: localConfigOverride takes precedence for immediate updates
  const cached = getSuperAppConfigSync(APP_ID);
  const cfg = APP_ID
    ? localConfigOverride ||
      (hookCfg && (hookCfg.config || hookCfg)) ||
      (cached && (cached.config || cached)) ||
      {}
    : {};
  void cfgVersion; // keep React aware that updates should re-render

  const headerTitle = cfg?.header?.title || null;
  const brandLogo = cfg?.header?.brand?.logoUrl;
  const features = cfg?.features || {};
  const flagShowLogin = features.showLogin !== false; // default true
  const flagShowTrial = features.showTryForFree !== false; // default true

  /* Vik (Jan 13, 2026): Fetch available apps for the dropdown */
  const [appIdOptions, setAppIdOptions] = useState(
    Array.from(new Set([APP_ID, "Ben-test", "default"])),
  );

  useEffect(() => {
    async function fetchApps() {
      try {
        const { listSuperAppConfigs } = await import("../lib/superappConfig");
        const apps = await listSuperAppConfigs();
        if (Array.isArray(apps) && apps.length > 0) {
          const appIds = apps
            .map((app) => app.id || app._id || app.config?.appId)
            .filter(Boolean);
          // Combine with existing options and remove duplicates
          setAppIdOptions((prev) => Array.from(new Set([...prev, ...appIds])));
        }
      } catch (e) {
        console.error("Failed to fetch app list for header dropdown", e);
      }
    }

    if (isSystemAdmin(props) || isDev()) {
      fetchApps();
    }
  }, [props.userDeep]);

  useEffect(() => {
    if (headerTitle) document.title = headerTitle;
  }, [headerTitle]);

  // ---------- Build dynamic menu ----------
  const RESERVED = new Set([
    // "home", // Removed so Home can be configurable and ordered
    "agents",
    "help",
    "contact",
    "user_admin",
    "system_admin",
    "integrations",
    "marketing",
    "capabilities",
    "plan_table",
    "playground",
    "console",
    "tester",
    "menu-editor",
    "newmenu",
  ]);
  /* Vik (Jan 5, 2026) : We must check .length > 0 because cfg.header.primaryMenu.items can be an empty array [], which is truthy. If we don't check length, it blocks fallback to cfg.menu where new items are saved. */
  const menuItems =
    (Array.isArray(cfg?.header?.primaryMenu?.items) &&
      cfg.header.primaryMenu.items.length > 0 &&
      cfg.header.primaryMenu.items) ||
    (Array.isArray(cfg?.menu) && cfg.menu) ||
    (Array.isArray(cfg?.navigation) && cfg.navigation) ||
    [];

  // Ensure we always show a Home tab unless explicitly configured otherwise
  function withDefaultHome(items) {
    /* KAD: Commented out default Home injection as per user request
    const hasHome = (items || []).some((it) => {
      const k = String(it?.key || it?.label || "").toLowerCase();
      const p = String(it?.path || "").toLowerCase();
      return k === "home" || p === "/home";
    });
    if (hasHome) return items;
    return [
      { key: "home", label: "Home", path: "/home", order: -1, visible: true },
      ...(items || []),
    ];
    */
    return items || [];
  }

  const visibleSorted = withDefaultHome(menuItems)
    .filter((it) => it && it.visible !== false)
    .sort((a, b) => (a.order ?? 9999) - (b.order ?? 9999));

  // // Vik (Jan 15, 2026) : Debugging menu order
  // console.log(
  //   "Header Menu Items:",
  //   visibleSorted.map((i) => `${i.label} (${i.order})`)
  // );

  const dynamicMenu = visibleSorted
    .filter((m) => {
      // Filter out the old "newmenu" route (legacy menu editor page)
      // But allow "new-menu" or any other variation
      const key = String(m.key || "").toLowerCase();
      const path = String(m.path || "").toLowerCase();

      // Filter out Home as per user request
      if (key === "home" || path === "/home") return false;

      // Only filter exact match "newmenu" (no dashes) to avoid filtering legitimate items
      return !(key === "newmenu" && path === "/newmenu");
    })
    .filter((m) => {
      // Filter out reserved system routes, but allow user-created items
      const key = String(m.key || "").toLowerCase();
      // Only filter if it's an exact match in RESERVED set
      return !RESERVED.has(key);
    })
    .map((m) => {
      const key = String(m.key || m.label || "")
        .trim()
        .toLowerCase();
      const label = m.label || m.key || "Menu";
      const isExternal = !!m.url && !m.path;
      let to = m.path || (isExternal ? m.url : `/${key || "unknown"}`);

      /* Vik (Jan 9, 2026): Interchange paths as per user request. 
         If a dynamic item points to /menu-editor, redirect it to /newmenu. */
      if (to === "/menu-editor") {
        to = "/newmenu";
      }

      if (!isExternal && APP_ID && APP_ID !== "default" && !to.includes("?")) {
        to += `?appId=${APP_ID}`;
      } else if (
        !isExternal &&
        APP_ID &&
        APP_ID !== "default" &&
        to.includes("?")
      ) {
        to += `&appId=${APP_ID}`;
      }
      const target = m.target || (isExternal ? "_blank" : undefined);
      const children = Array.isArray(m.children)
        ? m.children
            .filter((c) => c && c.visible !== false)
            .sort((a, b) => (a.order ?? 9999) - (b.order ?? 9999))
        : [];
      return {
        key,
        label,
        to,
        isExternal,
        target,
        children,
        badge: m.badge,
        disabled: m.disabled,
        group: m.group || "left",
      };
    });

  // Group menu items by left/center/right
  const menuByGroup = {
    left: dynamicMenu.filter((m) => !m.group || m.group === "left"),
    center: dynamicMenu.filter((m) => m.group === "center"),
    right: dynamicMenu.filter((m) => m.group === "right"),
  };

  // ---------- Inline editor helpers ----------
  function getRootMenu(c) {
    // Prioritize header.primaryMenu.items (where we write) over legacy locations
    if (Array.isArray(c?.header?.primaryMenu?.items))
      return c.header.primaryMenu.items;
    if (Array.isArray(c?.menu)) return c.menu;
    if (Array.isArray(c?.navigation)) return c.navigation;
    return [];
  }

  function setRootMenu(c, items) {
    // Always write to header.primaryMenu.items (preferred location)
    const next = {
      ...c,
      header: {
        ...(c?.header || {}),
        primaryMenu: {
          ...(c?.header?.primaryMenu || {}),
          items: items,
        },
      },
    };
    // Clear legacy locations to avoid conflicts
    if (Array.isArray(next.menu)) next.menu = [];
    if (Array.isArray(next.navigation)) next.navigation = [];
    return next;
  }

  /**
   * Save config to server.
   *
   * CRITICAL: Must stringify functions before JSON.stringify()
   * JSON.stringify() silently drops functions, so we convert them to
   * { _functionString: "..." } format first.
   *
   * Uses optimistic updates - updates local state immediately, then syncs with server.
   *
   * @param {object} nextCfg - The config to save (may contain functions)
   */
  async function saveConfig(nextCfg) {
    setSaving(true);
    try {
      // Convert functions to { _functionString: "..." } format
      // This must happen BEFORE JSON.stringify() (which would drop functions)
      // Use _functionString instead of __function__ (Firestore reserves __ prefix)
      const stringifyFunctions = (obj) => {
        if (obj === null || obj === undefined) return obj;

        // Skip objects that are already in our stringified format (new format)
        if (
          typeof obj === "object" &&
          obj._functionString &&
          Object.keys(obj).length === 1
        ) {
          return obj; // Already stringified in new format, return as-is
        }

        // Convert old format (__function__) to new format (_functionString)
        // This handles data that was saved before we changed the format
        if (
          typeof obj === "object" &&
          obj.__function__ &&
          Object.keys(obj).length === 1
        ) {
          return { _functionString: obj.__function__ }; // Migrate old format to new format
        }

        if (typeof obj === "function") {
          return { _functionString: obj.toString() };
        }
        if (Array.isArray(obj)) {
          return obj.map(stringifyFunctions);
        }
        if (typeof obj === "object") {
          const result = {};
          for (const key in obj) {
            if (Object.prototype.hasOwnProperty.call(obj, key)) {
              result[key] = stringifyFunctions(obj[key]);
            }
          }
          return result;
        }
        return obj;
      };
      const prepared = stringifyFunctions(nextCfg);

      // Send to server
      const res = await fetch(
        `/api/superapp/config/${encodeURIComponent(APP_ID)}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ config: prepared }),
        },
      );
      const text = await res.text();
      const saved = res.ok ? JSON.parse(text) : { config: nextCfg };
      const finalCfg = saved.config || saved;

      // IMPORTANT: Keep localConfigOverride to preserve the exact config we just saved
      // Don't let server response overwrite it (server might not have functions restored)
      setSuperAppConfig(APP_ID, nextCfg); // Use nextCfg (our version) not finalCfg
      setLocalConfigOverride(nextCfg); // Keep override active
      // Skip re-fetch here; it can race and overwrite recent local edits
      window.dispatchEvent(
        new CustomEvent("superapp-config-updated", {
          detail: { appId: APP_ID },
        }),
      );
      setCfgVersion((v) => v + 1);
      setSaveMsg("Saved ✔");
    } catch {
      // If save fails, still update local state (optimistic update)
      setSuperAppConfig(APP_ID, nextCfg);
      setLocalConfigOverride(nextCfg);
      window.dispatchEvent(
        new CustomEvent("superapp-config-updated", {
          detail: { appId: APP_ID },
        }),
      );
      setCfgVersion((v) => v + 1);
      setSaveMsg("Saved locally ✔");
    }
    setSaving(false);
  }

  async function onAddMenuItem(e) {
    e.preventDefault();
    const label = newLabel.trim();
    if (!label) return;
    const key = label.toLowerCase().replace(/\s+/g, "-");
    const isExternal = /^https?:\/\//i.test(newPath);
    const to = isExternal
      ? undefined
      : `/${(newPath || key).replace(/^\//, "")}`;
    const url = isExternal ? newPath : undefined;
    const id = `${key}-${Date.now()}`;

    const root = [...getRootMenu(cfg)].map((item) => ({ ...item })); // clone
    const newItem = {
      id,
      key,
      label,
      path: to,
      url,
      order: Number(newOrder) || 0,
      visible: !!newVisible,
      target: newOpenInNewTab ? "_blank" : undefined,
      badge: newBadge.trim() || undefined,
      disabled: newDisabled || undefined,
      group: newGroup || undefined,
    };

    if (newParent) {
      const parentKeyLower = String(newParent).toLowerCase();
      const ix = root.findIndex(
        (m) => String(m.key || m.label || "").toLowerCase() === parentKeyLower,
      );
      if (ix >= 0) {
        const parent = {
          ...root[ix],
          children: [
            ...(Array.isArray(root[ix].children) ? root[ix].children : []),
            newItem,
          ],
        };
        root[ix] = parent;
      } else {
        root.push(newItem);
      }
    } else {
      root.push(newItem);
    }

    const nextCfg = setRootMenu(cfg, root);
    // Optimistic update
    setSuperAppConfig(APP_ID, nextCfg);
    setLocalConfigOverride(nextCfg);
    window.dispatchEvent(
      new CustomEvent("superapp-config-updated", { detail: { appId: APP_ID } }),
    );
    setCfgVersion((v) => v + 1);
    await saveConfig(nextCfg);
    // Keep the inline editor open after adding
    setShowAddMenu(true);
    setNewLabel("");
    setNewPath("");
    setNewParent("");
    setNewOrder(0);
    setNewVisible(true);
    setNewOpenInNewTab(false);
    setNewBadge("");
    setNewDisabled(false);
    setNewGroup("");
  }

  const handleMenuStructureChange = useCallback(
    async (nextMenuItems) => {
      const nextCfg = setRootMenu(cfg, nextMenuItems);
      setSuperAppConfig(APP_ID, nextCfg);
      setLocalConfigOverride(nextCfg);
      window.dispatchEvent(
        new CustomEvent("superapp-config-updated", {
          detail: { appId: APP_ID },
        }),
      );
      setCfgVersion((v) => v + 1);
      await saveConfig(nextCfg);
    },
    [APP_ID, cfg, saveConfig],
  );

  // Handle saving from the new Menu Builder Modal
  const handleMenuBuilderSave = async (itemData) => {
    const { label, path, parentId, visible, target } = itemData;
    const key = label.toLowerCase().replace(/\s+/g, "-");
    const id = `${key}-${Date.now()}`;

    const newItem = {
      id,
      key,
      label,
      path,
      visible,
      target,
      order: 9999, // Append to end
    };

    const root = [...getRootMenu(cfg)].map((item) => ({ ...item })); // clone

    if (parentId) {
      // Find parent and add child
      const findAndAdd = (items) => {
        for (let i = 0; i < items.length; i++) {
          if (items[i].key === parentId || items[i].label === parentId) {
            items[i].children = [...(items[i].children || []), newItem];
            return true;
          }
          if (items[i].children) {
            if (findAndAdd(items[i].children)) return true;
          }
        }
        return false;
      };
      findAndAdd(root);
    } else {
      // Add to root
      root.push(newItem);
    }

    const nextCfg = setRootMenu(cfg, root);
    // Optimistic update
    setSuperAppConfig(APP_ID, nextCfg);
    setLocalConfigOverride(nextCfg);
    window.dispatchEvent(
      new CustomEvent("superapp-config-updated", { detail: { appId: APP_ID } }),
    );
    setCfgVersion((v) => v + 1);
    await saveConfig(nextCfg);
  };

  // Handle creating a new app from the header dropdown
  const handleCreateApp = async (e) => {
    e.preventDefault();
    if (!createAppName || !createAppId) return;

    setCreatingApp(true);
    try {
      const selectedTheme =
        THEME_PRESETS.find((t) => t.id === createAppTheme) || THEME_PRESETS[0];

      // Basic initial config template
      const initialConfig = {
        header: {
          title: createAppName,
          brand: { logoUrl: "/images/logo.png" },
          primaryMenu: { items: [] },
        },
        features: {
          showLogin: true,
          showTryForFree: false,
        },
        screens: {
          home: {
            type: "hero",
            title: `Welcome to ${createAppName}`,
            subtitle: "This is your new app. Start building!",
            cta: { label: "Get Started", href: "/docs" },
          },
        },
        theme: selectedTheme,
      };

      await createSuperAppConfig(createAppId, initialConfig);

      // Redirect to the new app
      window.location.href = `/?appId=${encodeURIComponent(createAppId)}`;
    } catch (e) {
      console.error("Failed to create app", e);
      alert("Failed to create app: " + e.message);
    } finally {
      setCreatingApp(false);
    }
  };

  // ---------- Auth / session ----------
  useEffect(() => {
    const currentPath = location.pathname || "/home";
    setActiveMenu(currentPath);

    async function hydrate() {
      if (user && user.email && !member && props.callback) {
        props.callback(user.email);
      }
      if (user?.email) {
        setMember(user.email);
        const sessionId = authConfig.sessionId;
        if (sessionId && sessionId.length > 0) {
          try {
            const response = await fetchSecure(
              "/api/getSession/" + encodeURIComponent(sessionId),
            );
            const j = await response.json();
            setMember(j?.userId || user.email);
          } catch (e) {
            console.error("Session fetch failed:", e);
          }
        }
      } else if (!loading) {
        setMember(null);
      }
    }
    hydrate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loading, authConfig.sessionId, location.pathname]);

  // Marketing
  useEffect(() => {
    async function loadMarketing() {
      if (props.userDeep && Object.keys(props.userDeep).length !== 0) {
        const r = await fetchSecure("/api/getMarketing");
        const all = await r.json();
        const clicked = props.userDeep.messagesClicked;
        if (clicked) {
          setMarketingMessages(
            all.filter((m) => !clicked.some((x) => x.messageId === m.id)),
          );
        } else {
          setMarketingMessages(all);
        }
      }
    }
    loadMarketing();
  }, [props.userDeep]);

  useEffect(() => setExpanded(false), [location.pathname, user]);

  function isDev() {
    const href = window.location.href || "";
    return (
      href.includes("local") ||
      href.includes("dev") ||
      href.includes("localhost") ||
      href.includes("superapp-479920")
    );
  }
  function isSystemAdmin(props) {
    if (props.userDeep && props.userDeep.roles) {
      if (Array.isArray(props.userDeep.roles)) {
        return props.userDeep.roles.includes("system_admin");
      }
      return props.userDeep.roles.toLowerCase().indexOf("system_admin") >= 0;
    }
    return false;
  }
  function isTester(p) {
    const roles = p?.userDeep?.roles || "";
    return roles.toLowerCase().includes("tester");
  }
  function isLoggedIn() {
    return !!(user && user.email);
  }
  const capitalize = (s) =>
    !s ? "" : s.substring(0, 1).toUpperCase() + s.substring(1);

  const handleMarketingClick = async (messageId) => {
    const date = new Date().toISOString();
    const clickedMessage = { messageId, timeStamp: date };

    const current = props.userDeep?.messagesClicked || [];
    const updatedMessagesClicked = [
      ...current,
      ...clickedMessages,
      clickedMessage,
    ];
    setClickedMessages(updatedMessagesClicked);

    const updatedUser = {
      ...props.userDeep,
      messagesClicked: updatedMessagesClicked,
    };

    const response = await fetchSecure("/api/saveObject/users", {
      headers: { "Content-Type": "application/json" },
      method: "post",
      body: JSON.stringify(updatedUser),
    });
    const j = await response.json();
    if (j.success) {
      setMarketingMessages((x) => x.filter((m) => m.id !== messageId));
    }
  };

  const handleNavClick = (menuTo) => {
    setActiveMenu(menuTo);
    const ip = data["ipAddress"] || "localhost";
    const city = data["city"];
    const country = data["country"];
    sendChatMessage(
      `Clicked on ${menuTo} by ${ip}${
        ip !== "localhost" ? ` in ${city},${country}` : ""
      } (${data["environmentName"]})`,
    );
    setExpanded(false);
  };

  const showAd = () =>
    [
      "/home",
      "/capabilities",
      "/whitepapers",
      "/contact",
      "/plan_table",
    ].includes(activeMenu);

  return (
    <>
      {location.pathname !== "/signin" && (
        <Navbar
          variant="light"
          expand="lg"
          expanded={expanded}
          onToggle={() => setExpanded(!expanded)}
          className="main-header"
          style={{ position: "relative" }}
        >
          <div className="custom-container" style={{ position: "relative" }}>
            {/* + Button at FAR LEFT - Only in Edit Mode */}
            {editMode && (
              <div
                style={{
                  position: "absolute",
                  left: "0px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 1050,
                }}
              >
                <button
                  className="primary-gradient-btn"
                  style={{
                    padding: "8px 12px",
                    borderRadius: "50%",
                    fontWeight: 800,
                    lineHeight: 1,
                    width: 40,
                    height: 40,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 4px 10px rgba(0,0,0,0.2)",
                  }}
                  onClick={() => setShowMenuBuilder(true)}
                  title="Add Menu Item"
                >
                  <span style={{ fontSize: 20, marginTop: -2 }}>+</span>
                </button>
              </div>
            )}

            {/* Menu Builder Modal */}
            <MenuBuilderModal
              show={showMenuBuilder}
              onHide={() => setShowMenuBuilder(false)}
              onSave={handleMenuBuilderSave}
              parentOptions={visibleSorted.map((m) => ({
                key: m.key || m.label,
                label: m.label || m.key,
              }))}
            />

            <div
              style={{
                marginLeft: editMode ? "50px" : "0",
                transition: "margin 0.3s ease",
                display: "flex",
                alignItems: "center",
              }}
            >
              <IndexLinkContainer
                to={APP_ID ? `/?appId=${APP_ID}` : "/"}
                activeClassName=""
              >
                <Navbar.Brand href="#">
                  {brandLogo ? (
                    <img
                      src={brandLogo}
                      alt={headerTitle || ""}
                      style={{
                        height: "2.25rem",
                        objectFit: "contain",
                        paddingRight: "1rem",
                      }}
                    />
                  ) : (
                    <img
                      src="/images/Koodums-logo-transparent.png"
                      alt={headerTitle || ""}
                      style={{
                        width: "12rem",
                        objectFit: "contain",
                        paddingRight: "1rem",
                      }}
                    />
                  )}
                </Navbar.Brand>
              </IndexLinkContainer>
            </div>

            <Navbar.Toggle aria-controls="basic-navbar-nav" />

            <Navbar.Collapse
              id="basic-navbar-nav"
              className={expanded ? "show" : ""}
            >
              <button
                type="button"
                className="close-btn"
                onClick={() => setExpanded(false)}
              >
                <AiOutlineClose />
              </button>

              <Nav className="mr-auto">
                {/* Home is now configurable via dynamicMenu - removed hardcoded link */}

                {/* Editor entry - show in dev mode AND only when logged in for security */}
                {isDev() && isLoggedIn() && (
                  <>
                    {/* <Nav.Link
                      as={Link}
                      to={
                        APP_ID ? `/menu-editor?appId=${APP_ID}` : "/menu-editor"
                      }
                      className={
                        activeMenu === "/menu-editor"
                          ? "active nav-link"
                          : "nav-link"
                      }
                      style={
                        activeMenu === "/menu-editor"
                          ? superAppStyle.menuActive
                          : superAppStyle.menuInactive
                      }
                      onClick={() =>
                        handleNavClick(
                          APP_ID
                            ? `/menu-editor?appId=${APP_ID}`
                            : "/menu-editor"
                        )
                      }
                    >
                      + Menu
                    </Nav.Link> */}
                    <Nav.Link
                      as={Link}
                      to={
                        APP_ID
                          ? `/unified-editor?appId=${APP_ID}`
                          : "/unified-editor"
                      }
                      className={
                        activeMenu === "/unified-editor"
                          ? "active nav-link"
                          : "nav-link"
                      }
                      style={
                        activeMenu === "/unified-editor"
                          ? superAppStyle.menuActive
                          : superAppStyle.menuInactive
                      }
                      onClick={() =>
                        handleNavClick(
                          APP_ID
                            ? `/unified-editor?appId=${APP_ID}`
                            : "/unified-editor",
                        )
                      }
                    >
                      + Unified Editor
                    </Nav.Link>
                    {/* <Nav.Link
                      as={Link}
                      to={
                        APP_ID ? `/page-editor?appId=${APP_ID}` : "/page-editor"
                      }
                      className={
                        activeMenu === "/page-editor"
                          ? "active nav-link"
                          : "nav-link"
                      }
                      style={
                        activeMenu === "/page-editor"
                          ? superAppStyle.menuActive
                          : superAppStyle.menuInactive
                      }
                      onClick={() =>
                        handleNavClick(
                          APP_ID
                            ? `/page-editor?appId=${APP_ID}`
                            : "/page-editor"
                        )
                      }
                    >
                      + Page
                    </Nav.Link> */}
                    {/* <Nav.Link
                      as={Link}
                      to={
                        APP_ID
                          ? `/header-manager?appId=${APP_ID}`
                          : "/header-manager"
                      }
                      className={
                        activeMenu === "/header-manager"
                          ? "active nav-link"
                          : "nav-link"
                      }
                      style={
                        activeMenu === "/header-manager"
                          ? superAppStyle.menuActive
                          : superAppStyle.menuInactive
                      }
                      onClick={() =>
                        handleNavClick(
                          APP_ID
                            ? `/header-manager?appId=${APP_ID}`
                            : "/header-manager"
                        )
                      }
                    >
                      Header builder
                    </Nav.Link> */}
                  </>
                )}

                {/* Dynamic items from SuperApp config - Left group */}
                {menuByGroup.left.map((it) => {
                  const renderLabel = () => (
                    <>
                      {it.label}
                      {it.badge && (
                        <span
                          style={{
                            marginLeft: 6,
                            fontSize: "0.7em",
                            background: "rgba(255,255,255,0.2)",
                            padding: "2px 6px",
                            borderRadius: 4,
                            fontWeight: 600,
                          }}
                        >
                          {it.badge}
                        </span>
                      )}
                    </>
                  );

                  if (Array.isArray(it.children) && it.children.length > 0) {
                    return (
                      <NavDropdown
                        key={`dyn-dd-${it.key}`}
                        title={renderLabel()}
                        id={`dd-${it.key}`}
                        disabled={it.disabled}
                      >
                        {it.children.map((c, idx) => {
                          const cKey = String(
                            c.key || c.label || `c${idx}`,
                          ).toLowerCase();
                          const cIsExternal = !!c.url && !c.path;
                          const cTo =
                            c.path ||
                            (cIsExternal ? c.url : `/${cKey || "unknown"}`);
                          if (cIsExternal || c.target === "_blank") {
                            return (
                              <NavDropdown.Item
                                key={`dyn-dd-item-${it.key}-${cKey}`}
                                href={cTo}
                                target={c.target || "_blank"}
                                rel="noopener noreferrer"
                                onClick={() => setExpanded(false)}
                              >
                                {c.label || c.key}
                              </NavDropdown.Item>
                            );
                          }
                          return (
                            <NavDropdown.Item
                              key={`dyn-dd-item-${it.key}-${cKey}`}
                              as={Link}
                              to={cTo}
                              onClick={() => handleNavClick(cTo)}
                            >
                              {c.label || c.key}
                            </NavDropdown.Item>
                          );
                        })}
                      </NavDropdown>
                    );
                  }

                  const linkStyle = it.disabled
                    ? {
                        ...superAppStyle.menuInactive,
                        opacity: 0.5,
                        cursor: "not-allowed",
                      }
                    : activeMenu === it.to
                      ? superAppStyle.menuActive
                      : superAppStyle.menuInactive;

                  return it.isExternal || it.target === "_blank" ? (
                    <Nav.Link
                      key={`dyn-${it.key}`}
                      href={it.to}
                      target={it.target || "_blank"}
                      rel={it.target === "_blank" ? "noopener noreferrer" : ""}
                      className="nav-link"
                      style={linkStyle}
                      onClick={(e) => {
                        if (it.disabled) e.preventDefault();
                        else setExpanded(false);
                      }}
                      disabled={it.disabled}
                    >
                      {renderLabel()}
                    </Nav.Link>
                  ) : (
                    <Nav.Link
                      key={`dyn-${it.key}`}
                      as={it.disabled ? "span" : Link}
                      to={it.to}
                      className={
                        activeMenu === it.to ? "active nav-link" : "nav-link"
                      }
                      style={linkStyle}
                      onClick={(e) => {
                        if (it.disabled) e.preventDefault();
                        else handleNavClick(it.to);
                      }}
                    >
                      {renderLabel()}
                    </Nav.Link>
                  );
                })}

                {/* Center group */}
                {menuByGroup.center.length > 0 && (
                  <div
                    style={{
                      marginLeft: "auto",
                      marginRight: "auto",
                      display: "flex",
                    }}
                  >
                    {menuByGroup.center.map((it) => {
                      const renderLabel = () => (
                        <>
                          {it.label}
                          {it.badge && (
                            <span
                              style={{
                                marginLeft: 6,
                                fontSize: "0.7em",
                                background: "rgba(255,255,255,0.2)",
                                padding: "2px 6px",
                                borderRadius: 4,
                                fontWeight: 600,
                              }}
                            >
                              {it.badge}
                            </span>
                          )}
                        </>
                      );
                      // Similar rendering as left group but centered
                      return it.isExternal || it.target === "_blank" ? (
                        <Nav.Link
                          key={`dyn-center-${it.key}`}
                          href={it.to}
                          target={it.target || "_blank"}
                          rel={
                            it.target === "_blank" ? "noopener noreferrer" : ""
                          }
                          className="nav-link"
                          style={
                            it.disabled
                              ? { opacity: 0.5 }
                              : superAppStyle.menuInactive
                          }
                          onClick={(e) => {
                            if (it.disabled) e.preventDefault();
                            else setExpanded(false);
                          }}
                          disabled={it.disabled}
                        >
                          {renderLabel()}
                        </Nav.Link>
                      ) : (
                        <Nav.Link
                          key={`dyn-center-${it.key}`}
                          as={it.disabled ? "span" : Link}
                          to={it.to}
                          className={
                            activeMenu === it.to
                              ? "active nav-link"
                              : "nav-link"
                          }
                          style={
                            it.disabled
                              ? { opacity: 0.5, cursor: "not-allowed" }
                              : activeMenu === it.to
                                ? superAppStyle.menuActive
                                : superAppStyle.menuInactive
                          }
                          onClick={(e) => {
                            if (it.disabled) e.preventDefault();
                            else handleNavClick(it.to);
                          }}
                        >
                          {renderLabel()}
                        </Nav.Link>
                      );
                    })}
                  </div>
                )}

                {/* Right group - rendered later after static items */}

                {/* Inline add (+) moved later to the far right */}
                {false && (isSystemAdmin(props) || isDev()) && (
                  <NavDropdown align="end" id="add-menu-inline-legacy">
                    <div />
                  </NavDropdown>
                )}

                {/* Logged-out static */}
                {!isLoggedIn() && !props.iframeLogin && (
                  <>
                    {/* <Nav.Link
                      as={Link}
                      to="/capabilities"
                      className={activeMenu === "/capabilities" ? "active nav-link" : "nav-link"}
                      style={
                        activeMenu === "/capabilities"
                          ? superAppStyle.menuActive
                          : superAppStyle.menuInactive
                      }
                      onClick={() => handleNavClick("/capabilities")}
                    >
                      Capabilities
                    </Nav.Link>
                    <Nav.Link
                      as={Link}
                      to="/plan_table"
                      className={activeMenu === "/plan_table" ? "active nav-link" : "nav-link"}
                      style={
                        activeMenu === "/plan_table"
                          ? superAppStyle.menuActive
                          : superAppStyle.menuInactive
                      }
                      onClick={() => handleNavClick("/plan_table")}
                    >
                      Pricing Plans
                    </Nav.Link>
                    <Nav.Link
                      as={Link}
                      to="/contact"
                      className={activeMenu === "/contact" ? "active nav-link" : "nav-link"}
                      style={
                        activeMenu === "/contact"
                          ? superAppStyle.menuActive
                          : superAppStyle.menuInactive
                      }
                      onClick={() => handleNavClick("/contact")}
                    >
                      Contact
                    </Nav.Link> */}
                  </>
                )}

                {/* Logged-in static */}
                {isLoggedIn() &&
                  !loading &&
                  props.userDeep &&
                  Object.keys(props.userDeep).length > 0 &&
                  props.userDeep.planName !== "unregistered" &&
                  !props?.isFinishRegistration && (
                    <>
                      {/* {isSystemAdmin(props) && isDev() && !props.iframeLogin && (
                        <Nav.Link
                          as={Link}
                          to="/playground"
                          className={activeMenu === "/playground" ? "active nav-link" : "nav-link"}
                          style={
                            activeMenu === "/playground"
                              ? superAppStyle.menuActive
                              : superAppStyle.menuInactive
                          }
                          onClick={() => handleNavClick("/playground")}
                        >
                          Playground
                        </Nav.Link>
                      )}
                      {isSystemAdmin(props) && isDev() && !props.iframeLogin && (
                        <Nav.Link
                          as={Link}
                          to="/console"
                          className={activeMenu === "/console" ? "active nav-link" : "nav-link"}
                          style={
                            activeMenu === "/console"
                              ? superAppStyle.menuActive
                              : superAppStyle.menuInactive
                          }
                          onClick={() => handleNavClick("/console")}
                        >
                          Console
                        </Nav.Link>
                      )}
                      <Nav.Link
                        as={Link}
                        to="/agents"
                        className={activeMenu === "/agents" ? "active nav-link" : "nav-link"}
                        style={
                          activeMenu === "/agents"
                            ? superAppStyle.menuActive
                            : superAppStyle.menuInactive
                        }
                        onClick={() => handleNavClick("/agents")}
                      >
                        Agents
                      </Nav.Link>
                      {isSystemAdmin(props) && !props.iframeLogin && (
                        <Nav.Link
                          as={Link}
                          to="/integrations"
                          className={activeMenu === "/integrations" ? "active nav-link" : "nav-link"}
                          style={
                            activeMenu === "/integrations"
                              ? superAppStyle.menuActive
                              : superAppStyle.menuInactive
                          }
                          onClick={() => handleNavClick("/integrations")}
                        >
                          Integrations
                        </Nav.Link>
                      )} */}
                      {/* {isSystemAdmin(props) && !props.iframeLogin && (
                        <Nav.Link
                          as={Link}
                          to="/marketing"
                          className={activeMenu === "/marketing" ? "active nav-link" : "nav-link"}
                          style={
                            activeMenu === "/marketing"
                              ? superAppStyle.menuActive
                              : superAppStyle.menuInactive
                          }
                          onClick={() => handleNavClick("/marketing")}
                        >
                          AI Marketing
                        </Nav.Link>
                      )} */}
                      {/* {isSystemAdmin(props) && !props.iframeLogin && (
                        <Nav.Link
                          as={Link}
                          to={APP_ID ? `/newmenu?appId=${APP_ID}` : "/newmenu"}
                          className={
                            activeMenu === "/newmenu"
                              ? "active nav-link"
                              : "nav-link"
                          }
                          style={
                            activeMenu === "/newmenu"
                              ? superAppStyle.menuActive
                              : superAppStyle.menuInactive
                          }
                          onClick={() =>
                            handleNavClick(
                              APP_ID ? `/newmenu?appId=${APP_ID}` : "/newmenu"
                            )
                          }
                        >
                          New Menu
                        </Nav.Link>
                      )} */}
                      {/* <Nav.Link
                        as={Link}
                        to="/help"
                        className={activeMenu === "/help" ? "active nav-link" : "nav-link"}
                        style={
                          activeMenu === "/help"
                            ? superAppStyle.menuActive
                            : superAppStyle.menuInactive
                        }
                        onClick={() => handleNavClick("/help")}
                      >
                        Help
                      </Nav.Link> */}
                      {/* <Nav.Link
                        as={Link}
                        to="/contact"
                        className={activeMenu === "/contact" ? "active nav-link" : "nav-link"}
                        style={
                          activeMenu === "/contact"
                            ? superAppStyle.menuActive
                            : superAppStyle.menuInactive
                        }
                        onClick={() => handleNavClick("/contact")}
                      >
                        Contact
                      </Nav.Link> */}
                      {/* <Nav.Link
                        as={Link}
                        to="/user_admin"
                        className={activeMenu === "/user_admin" ? "active nav-link" : "nav-link"}
                        style={
                          activeMenu === "/user_admin"
                            ? superAppStyle.menuActive
                            : superAppStyle.menuInactive
                        }
                        onClick={() => handleNavClick("/user_admin")}
                      >
                        Admin
                      </Nav.Link> */}
                      {/* {isSystemAdmin(props) && !props.iframeLogin && (
                        <Nav.Link
                          as={Link}
                          to="/system_admin"
                          className={
                            activeMenu === "/system_admin" ? "active nav-link" : "nav-link"
                          }
                          style={
                            activeMenu === "/system_admin"
                              ? superAppStyle.menuActive
                              : superAppStyle.menuInactive
                          }
                          onClick={() => handleNavClick("/system_admin")}
                        >
                          System Admin
                        </Nav.Link>
                      )} */}
                      {/* {isTester(props) && (
                        <Nav.Link
                          as={Link}
                          to="/tester"
                          className={activeMenu === "/tester" ? "active nav-link" : "nav-link"}
                          style={
                            activeMenu === "/tester"
                              ? superAppStyle.menuActive
                              : superAppStyle.menuInactive
                          }
                          onClick={() => handleNavClick("/tester")}
                        >
                          Tester
                        </Nav.Link>
                      )} */}
                    </>
                  )}

                {/* Badges */}
                <Nav.Item>
                  <div style={superAppStyle.headerText}>
                    {props.userDeep &&
                      Object.keys(props.userDeep).length > 0 && (
                        <span className="nav-link info-text">
                          Plan: {capitalize(props.userDeep["planName"])}
                        </span>
                      )}
                  </div>
                </Nav.Item>

                {(isSystemAdmin(props) || isDev()) && user && (
                  <NavDropdown
                    title={
                      <span className="app-switcher-title">
                        {APP_ID || "Select App"}
                      </span>
                    }
                    id="app-switcher-dropdown"
                    className="app-switcher-dropdown"
                    align="end"
                  >
                    {appIdOptions.map((opt) => (
                      <NavDropdown.Item
                        key={opt}
                        active={opt === APP_ID}
                        onClick={() => {
                          const url = new URL(window.location.href);
                          url.searchParams.set("appId", opt);
                          window.location.href = url.toString();
                        }}
                      >
                        {opt}
                      </NavDropdown.Item>
                    ))}
                    <NavDropdown.Divider />
                    <NavDropdown.Item
                      onClick={() => setShowCreateAppModal(true)}
                      className="create-app-option"
                    >
                      <FaPlus style={{ marginRight: 8 }} />
                      Create New App
                    </NavDropdown.Item>
                  </NavDropdown>
                )}

                {isLoggedIn() &&
                  data["environmentName"] &&
                  data["environmentName"] !== "Production" && (
                    <Nav.Item>
                      <span
                        className="nav-link info-text"
                        style={superAppStyle.headerText}
                      >
                        {data["environmentName"]}
                      </span>
                    </Nav.Item>
                  )}

                {/* Right group */}
                {menuByGroup.right.map((it) => {
                  const renderLabel = () => (
                    <>
                      {it.label}
                      {it.badge && (
                        <span
                          style={{
                            marginLeft: 6,
                            fontSize: "0.7em",
                            background: "rgba(255,255,255,0.2)",
                            padding: "2px 6px",
                            borderRadius: 4,
                            fontWeight: 600,
                          }}
                        >
                          {it.badge}
                        </span>
                      )}
                    </>
                  );
                  return it.isExternal || it.target === "_blank" ? (
                    <Nav.Link
                      key={`dyn-right-${it.key}`}
                      href={it.to}
                      target={it.target || "_blank"}
                      rel={it.target === "_blank" ? "noopener noreferrer" : ""}
                      className="nav-link"
                      style={
                        it.disabled
                          ? { opacity: 0.5 }
                          : superAppStyle.menuInactive
                      }
                      onClick={(e) => {
                        if (it.disabled) e.preventDefault();
                        else setExpanded(false);
                      }}
                      disabled={it.disabled}
                    >
                      {renderLabel()}
                    </Nav.Link>
                  ) : (
                    <Nav.Link
                      key={`dyn-right-${it.key}`}
                      as={it.disabled ? "span" : Link}
                      to={it.to}
                      className={
                        activeMenu === it.to ? "active nav-link" : "nav-link"
                      }
                      style={
                        it.disabled
                          ? { opacity: 0.5, cursor: "not-allowed" }
                          : activeMenu === it.to
                            ? superAppStyle.menuActive
                            : superAppStyle.menuInactive
                      }
                      onClick={(e) => {
                        if (it.disabled) e.preventDefault();
                        else handleNavClick(it.to);
                      }}
                    >
                      {renderLabel()}
                    </Nav.Link>
                  );
                })}

                {/* Right side auth area - only show when logged in AND (system admin OR dev mode) */}
                {/* Note: + button moved to left side above */}
                {/* Edit Mode Toggle - Only for logged in users */}
                {isLoggedIn() && (
                  <Nav.Item>
                    <div
                      className="nav-link"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      {/* Theme Picker Button - Only visible in Edit Mode */}
                      {editMode && (
                        <div
                          className="theme-picker-btn"
                          onClick={() => setShowThemePicker(true)}
                          title="Change Theme"
                          style={{
                            cursor: "pointer",
                            padding: "6px 12px",
                            borderRadius: "20px",
                            background: "rgba(0,0,0,0.05)",
                            color: "#666",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            transition: "all 0.2s ease",
                            border: "1px solid rgba(0,0,0,0.1)",
                          }}
                        >
                          <FaPalette />
                          <span style={{ fontSize: "0.85em", fontWeight: 600 }}>
                            Theme
                          </span>
                        </div>
                      )}

                      <div
                        className={`edit-mode-toggle ${
                          editMode ? "active" : ""
                        }`}
                        onClick={() => setEditMode(!editMode)}
                        title={editMode ? "Exit Edit Mode" : "Enter Edit Mode"}
                        style={{
                          cursor: "pointer",
                          padding: "6px 12px",
                          borderRadius: "20px",
                          background: editMode ? "#764ba2" : "rgba(0,0,0,0.05)",
                          color: editMode ? "#fff" : "#666",
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          transition: "all 0.2s ease",
                          border: "1px solid",
                          borderColor: editMode ? "#764ba2" : "rgba(0,0,0,0.1)",
                        }}
                      >
                        {editMode ? <FaEdit /> : <FaEye />}
                        <span style={{ fontSize: "0.85em", fontWeight: 600 }}>
                          {editMode ? "Edit Mode" : "View Mode"}
                        </span>
                      </div>
                    </div>
                  </Nav.Item>
                )}

                <ThemePickerModal
                  show={showThemePicker}
                  onHide={() => setShowThemePicker(false)}
                />

                {/* Create New App Modal */}
                <Modal
                  show={showCreateAppModal}
                  onHide={() => {
                    setShowCreateAppModal(false);
                    setCreateAppName("");
                    setCreateAppId("");
                    setCreateAppTheme("purple");
                  }}
                  centered
                  className="premium-modal create-app-modal"
                >
                  <Modal.Header closeButton>
                    <Modal.Title>Create New App</Modal.Title>
                  </Modal.Header>
                  <Modal.Body>
                    <Form onSubmit={handleCreateApp}>
                      <Form.Group className="mb-3">
                        <Form.Label>App Name</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="e.g. Employee Portal"
                          value={createAppName}
                          onChange={(e) => {
                            setCreateAppName(e.target.value);
                            // Auto-generate ID from name if not manually set
                            if (
                              !createAppId ||
                              createAppId ===
                                createAppName.toLowerCase().replace(/\s+/g, "-")
                            ) {
                              setCreateAppId(
                                e.target.value
                                  .toLowerCase()
                                  .replace(/\s+/g, "-"),
                              );
                            }
                          }}
                          required
                        />
                      </Form.Group>
                      <Form.Group className="mb-3">
                        <Form.Label>App ID (Unique)</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="e.g. employee-portal-v1"
                          value={createAppId}
                          onChange={(e) => setCreateAppId(e.target.value)}
                          required
                        />
                        <Form.Text className="text-muted">
                          This will be used in the URL: ?appId=...
                        </Form.Text>
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Label>Theme</Form.Label>
                        <div className="theme-selector">
                          {THEME_PRESETS.map((theme) => (
                            <div
                              key={theme.id}
                              className={`theme-option ${createAppTheme === theme.id ? "selected" : ""}`}
                              onClick={() => setCreateAppTheme(theme.id)}
                            >
                              <div
                                className="theme-preview"
                                style={{
                                  background: `linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.secondary} 100%)`,
                                }}
                              ></div>
                              <span>{theme.name}</span>
                            </div>
                          ))}
                        </div>
                      </Form.Group>

                      <div className="d-flex justify-content-end mt-4">
                        <Button
                          variant="secondary"
                          onClick={() => {
                            setShowCreateAppModal(false);
                            setCreateAppName("");
                            setCreateAppId("");
                            setCreateAppTheme("purple");
                          }}
                          style={{ marginRight: "16px" }}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="submit"
                          className="primary-gradient-btn"
                          disabled={creatingApp}
                        >
                          {creatingApp ? "Creating..." : "Create App"}
                        </Button>
                      </div>
                    </Form>
                  </Modal.Body>
                </Modal>

                {/* Exit App Button - Moved here */}
                {APP_ID && APP_ID !== "default" && APP_ID !== "Ben-test" && (
                  <Nav.Item className="d-flex align-items-center mr-3">
                    <a
                      href="/"
                      style={{
                        color: "rgba(255,255,255,0.7)",
                        fontSize: "0.9rem",
                        textDecoration: "none",
                        border: "1px solid rgba(255,255,255,0.2)",
                        padding: "6px 12px",
                        borderRadius: "20px",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        background: "rgba(255,255,255,0.05)",
                        transition: "all 0.2s",
                      }}
                      title="Exit App"
                      className="exit-app-btn"
                      onClick={(e) => {
                        // e.preventDefault(); // Default behavior is fine (navigates to /)
                        // localStorage.removeItem("superAppId"); // Removed
                      }}
                    >
                      <AiOutlineLogout size={16} />
                      <span style={{ fontWeight: 500 }}>Exit</span>
                    </a>
                  </Nav.Item>
                )}

                <Nav.Item>
                  {!props.iframeLogin && (
                    <div className="nav-link">
                      {isLoggedIn() ? (
                        <figure
                          className="after-login-user-info"
                          onClick={() => setAfterLoginUserInfo((v) => !v)}
                        >
                          <div className="user-icon">
                            <FaCircleUser />
                          </div>
                          <span
                            className={
                              afterLoginUserInfo ? "arow active" : "arow"
                            }
                          >
                            <FaAngleDown />
                          </span>
                          {afterLoginUserInfo && (
                            <>
                              <div className="after-login-user-dropdown">
                                <figure className="user-info">
                                  <h4>{member}</h4>
                                </figure>
                                <div className="dropdown--item">
                                  <SuperAppButton
                                    className="dropdown-btn primary-gradient-btn"
                                    onClick={async () => {
                                      auth
                                        .signOut()
                                        .then(() => {
                                          logUserAction(
                                            member,
                                            "Logged Out",
                                            "",
                                          );
                                          sessionStorage.removeItem(
                                            "sessionId",
                                          );
                                          localStorage.removeItem("token");
                                          localStorage.removeItem("userName");
                                          localStorage.removeItem("wixToken");
                                          localStorage.removeItem("isLoggedIn");
                                          localStorage.removeItem(
                                            "pageEditor_editMode",
                                          );
                                          // localStorage.removeItem("superAppId"); // Removed
                                          setMember(null);
                                          sessionStorage.removeItem(
                                            "userDeepObj",
                                          );
                                          // Redirect to root after sign out
                                          window.location.replace("/");
                                        })
                                        .catch((error) => {
                                          console.error(
                                            "Sign out error:",
                                            error,
                                          );
                                          localStorage.removeItem("isLoggedIn");
                                          localStorage.removeItem(
                                            "pageEditor_editMode",
                                          );
                                          // localStorage.removeItem("superAppId"); // Removed
                                          // Still redirect even if signOut fails
                                          window.location.replace("/");
                                        });
                                    }}
                                  >
                                    Log Out
                                  </SuperAppButton>
                                </div>
                              </div>
                              <div
                                className="after-login-user-dropdown-overlay"
                                onClick={() => setAfterLoginUserInfo(false)}
                              ></div>
                            </>
                          )}
                        </figure>
                      ) : (
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          {flagShowLogin && (
                            <SuperAppButton
                              className="primary-gradient-btn"
                              size="sm"
                              onClick={() => {
                                props?.setFinishRegistration?.(false);
                                props.history.push("/signin");
                              }}
                            >
                              Login
                            </SuperAppButton>
                          )}
                          <div style={{ width: 5 }} />
                          {flagShowTrial && (
                            <SuperAppButton
                              className="primary-gradient-btn"
                              size="sm"
                              onClick={() => {
                                props?.setFinishRegistration?.(false);
                                props.history.push("/signin");
                              }}
                            >
                              Try for Free
                            </SuperAppButton>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </Nav.Item>
              </Nav>
            </Navbar.Collapse>
          </div>
        </Navbar>
      )}

      {/* Marketing messages */}
      {props.userDeep?.planName !== "unregistered" &&
        Array.isArray(marketingMessages) &&
        marketingMessages.length > 0 &&
        marketingMessages.map((message) => (
          <div key={message.id} className="advertisement-alert">
            <div className="custom-container">
              <div className="advertisement-alert-wrapper">
                <div className="alert-text">
                  <p dangerouslySetInnerHTML={{ __html: message.message }}></p>
                </div>
                <div className="alert-action">
                  <button
                    type="button"
                    className="closebtn"
                    onClick={() => handleMarketingClick(message.id)}
                  >
                    <IoMdClose />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}

      {(() => {
        const msgs = data?.marketingMessages;
        return (
          showAd() &&
          Array.isArray(msgs) &&
          msgs.length > 0 &&
          msgs.map((m, i) => (
            <div key={i} className="advertisement-alert">
              <div className="custom-container">
                <div className="advertisement-alert-wrapper">
                  <div className="alert-text">
                    <p dangerouslySetInnerHTML={{ __html: m }}></p>
                  </div>
                </div>
              </div>
            </div>
          ))
        );
      })()}
    </>
  );
}

export default withRouter(Header);
